import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../config/api";
import CryptoJS from "crypto-js"; // You'll need to install this: npm install crypto-js

// Secret key for encryption (in a real app, use an environment variable)
const SECRET_KEY = "KJSCE-Project-Allocation-System-2025";

// Create the Auth Context
const AuthContext = createContext(null);

// Provider component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [activeRole, setActiveRole] = useState(null);
    const [userRoles, setUserRoles] = useState([]);
    const [thrustArea, setThrustArea] = useState("");
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [authToken, setAuthToken] = useState(null);

    // Initialize from session storage
    useEffect(() => {
        const init = async () => {
            try {
                // Try to get encrypted auth data from session storage
                const encryptedAuthData = sessionStorage.getItem("auth_data");

                if (!encryptedAuthData) {
                    // console.log("No auth data found in session storage");
                    setLoading(false);
                    return;
                }

                // Decrypt the auth data
                try {
                    const decryptedBytes = CryptoJS.AES.decrypt(
                        encryptedAuthData,
                        SECRET_KEY
                    );
                    const authDataStr = decryptedBytes.toString(
                        CryptoJS.enc.Utf8
                    );

                    if (!authDataStr) {
                        console.error("Failed to decrypt auth data");
                        await logout();
                        return;
                    }

                    const authData = JSON.parse(authDataStr);

                    // Verify the auth data has all required fields
                    if (
                        !authData.user ||
                        !authData.role ||
                        !authData.token ||
                        !authData.timestamp
                    ) {
                        console.error("Invalid auth data format");
                        await logout();
                        return;
                    }

                    const expiryTime = 8 * 60 * 60 * 1000;
                    const isExpired =
                        Date.now() - authData.timestamp > expiryTime;

                    if (isExpired) {
                        console.error("Auth token expired");
                        await logout();
                        return;
                    }

                    // Set auth state
                    setUser(authData.user);
                    setActiveRole(authData.role);
                    setAuthToken(authData.token);
                    setIsAuthenticated(true);

                    // Configure axios to use the token for future requests
                    axios.defaults.headers.common[
                        "Authorization"
                    ] = `Bearer ${authData.token}`;

                    // Skip token verification for now since it's causing issues
                    // Instead, load roles directly if email exists
                    if (authData.user.email) {
                        try {
                            await fetchUserRoles(authData.user.email);
                        } catch (roleError) {
                            console.error("Error fetching roles:", roleError);
                            // Don't log out here, just continue with existing data
                        }
                    }
                } catch (decryptionError) {
                    console.error(
                        "Auth data decryption failed:",
                        decryptionError
                    );
                    await logout();
                }
            } catch (error) {
                console.error("Error initializing auth:", error);
                await logout();
            } finally {
                setLoading(false);
            }
        };

        init();
    }, []);

    // Fetch user roles
    const fetchUserRoles = async (email, token = null) => {
        try {
            // console.log("Fetching roles for email:", email);

            const authHeader = token ? `Bearer ${token}` :
                (user?.token ? `Bearer ${user.token}` :
                    axios.defaults.headers.common["Authorization"]);

            const response = await axios.get(
                `${API_BASE_URL}/api/faculty/email/${email}/roles`,
                {
                    headers: {
                        Authorization: authHeader,
                    },
                }
            );

            // console.log("Roles API response:", response.data);

            if (response.data.success) {
                setUserRoles(response.data.roles || []);
                setThrustArea(response.data.thrustArea || "");
                return response.data.roles || [];
            }
            return [];
        } catch (error) {
            console.error("Error fetching roles:", error);
            console.error("Response data:", error.response?.data);
            console.error("Status:", error.response?.status);
            return [];
        }
    };

    // Verify the user can use the specified role
    const verifyRoleAccess = async (role) => {
        try {
            // console.log("Verifying access for role:", role);
            // console.log(
            //     "Authorization header:",
            //     axios.defaults.headers.common["Authorization"]
            // );

            // Get the role verification from backend
            const response = await axios.get(
                `${API_BASE_URL}/api/auth/verify-role-access?role=${role}`
            );

            // console.log("Role verification response:", response.data);

            return response.data.hasAccess === true;
        } catch (error) {
            console.error("Error verifying role access:", error);
            console.error("Response data:", error.response?.data);
            console.error("Status:", error.response?.status);
            return false;
        }
    };

    // Map backend role to frontend role
    const mapRoleToUI = (role) => {
        switch (role) {
            case "admin":
                return "admin";
            case "guide":
                return "guide";
            case "thrustarea coordinator":
                return "thrustarea";
            case "examiner":
                return "examiner";
            case "project coordinator":
                return "pc";
            default:
                return role;
        }
    };

    // Save encrypted auth data to session storage
    const saveAuthData = (userData, role, token) => {
        const authData = {
            user: userData,
            role: role,
            token: token,
            timestamp: Date.now(),
        };

        // Encrypt the auth data
        const encryptedAuthData = CryptoJS.AES.encrypt(
            JSON.stringify(authData),
            SECRET_KEY
        ).toString();

        // Save to session storage
        sessionStorage.setItem("auth_data", encryptedAuthData);
    };

    // Login function
    const login = async (userData, initialRole) => {
        try {
            // Check if token is in the userData
            const token = userData.token;

            if (!token) {
                console.error("No authentication token received from server");
                throw new Error("Authentication failed: No token received");
            }

            // Set the user in state
            setUser(userData);
            setIsAuthenticated(true);

            // Save the token and configure axios
            setAuthToken(token);
            axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

            // console.log("Auth token set:", token);

            let roleToUse = initialRole;

            // For faculty, fetch all roles
            if (
                userData.email &&
                (userData.roles || initialRole === "faculty")
            ) {
                const roles = await fetchUserRoles(userData.email, token);
                // console.log("Fetched roles:", roles);

                // If we have roles, set the active role to the first one or the provided initial role
                if (roles.length > 0) {
                    const mappedRoles = roles.map(mapRoleToUI);

                    // If initialRole is valid, use it; otherwise use the first role
                    if (initialRole && mappedRoles.includes(initialRole)) {
                        roleToUse = initialRole;
                    } else {
                        // Default role priority: admin > guide > thrustarea > pc > examiner > faculty
                        if (mappedRoles.includes("admin")) {
                            roleToUse = "admin";
                        } else if (mappedRoles.includes("guide")) {
                            roleToUse = "guide";
                        } else if (mappedRoles.includes("thrustarea")) {
                            roleToUse = "thrustarea";
                        } else if (mappedRoles.includes("pc")) {
                            roleToUse = "pc";
                        } else if (mappedRoles.includes("examiner")) {
                            roleToUse = "examiner";
                        } else {
                            roleToUse = "faculty";
                        }
                    }

                    // Verify the user can use this role
                    // console.log("Verifying access for role:", roleToUse);
                    const canUseRole = await verifyRoleAccess(roleToUse);

                    if (!canUseRole) {
                        console.error(
                            "User doesn't have permission to use role:",
                            roleToUse
                        );

                        // Try to find a role they can use
                        for (const alternateRole of mappedRoles) {
                            const canUseAlternate = await verifyRoleAccess(
                                alternateRole
                            );
                            if (canUseAlternate) {
                                // console.log(
                                //    "Using alternate role:",
                                //    alternateRole
                                //);
                                roleToUse = alternateRole;
                                break;
                            }
                        }

                        // If still no valid role, throw error
                        if (roleToUse === initialRole) {
                            throw new Error("No valid role found for user");
                        }
                    }
                }
            }

            // console.log("Using role:", roleToUse);

            // Set the active role
            setActiveRole(roleToUse);

            // Save encrypted auth data with token
            saveAuthData(userData, roleToUse, token);

            return roleToUse;
        } catch (error) {
            console.error("Login error:", error);
            await logout();
            throw error;
        }
    };

    // Logout function
    const logout = async () => {
        try {
            // Call backend logout endpoint if available
            if (authToken) {
                try {
                    await axios.post(`${API_BASE_URL}/api/auth/logout`);
                } catch (logoutError) {
                    console.error("Logout API error:", logoutError);
                }
            }

            // Reset auth state
            setUser(null);
            setActiveRole(null);
            setUserRoles([]);
            setThrustArea("");
            setIsAuthenticated(false);
            setAuthToken(null);

            // Remove auth header
            delete axios.defaults.headers.common["Authorization"];

            // Clear session storage
            sessionStorage.removeItem("auth_data");
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    // Switch role function
    const switchRole = async (newRole) => {
        try {
            // console.log("Switching to role:", newRole);

            // Ensure the role is valid for UI display
            if (!newRole) {
                console.error("Attempted to switch to undefined role");
                throw new Error("Invalid role");
            }

            // First verify the user can use this role
            // Use a fallback approach in case the API call fails
            let canUseRole = false;

            try {
                canUseRole = await verifyRoleAccess(newRole);
                // console.log(
                //     `API verification for role ${newRole}: ${canUseRole}`
                // );
            } catch (verifyError) {
                console.error(
                    "Error in role verification API call:",
                    verifyError
                );

                // Fall back to checking local roles array
                if (userRoles && userRoles.length > 0) {
                    const mappedRoles = userRoles.map(mapRoleToUI);
                    canUseRole = mappedRoles.includes(newRole);
                    // console.log(
                    //     `Fallback local verification for role ${newRole}: ${canUseRole}`
                    // );
                }
            }

            if (!canUseRole) {
                console.error(
                    "User doesn't have permission to use role:",
                    newRole
                );
                throw new Error(
                    `You don't have permission to use the ${newRole} role`
                );
            }

            // Update state first - this is important to avoid UI flickering
            setActiveRole(newRole);

            // Then save to storage
            try {
                saveAuthData(user, newRole, authToken);
                // console.log("Auth data saved with new role:", newRole);
            } catch (saveError) {
                console.error("Error saving auth data:", saveError);
                // Continue anyway - the state is already updated
            }

            return newRole;
        } catch (error) {
            console.error("Role switch error:", error);
            // Don't throw here - this prevents navigation and causes logout
            // Instead, return the current role to indicate no change
            return activeRole;
        }
    };

    // Check if user has a specific role
    const hasRole = (role) => {
        if (!userRoles || userRoles.length === 0) return false;

        // For exact match
        if (userRoles.includes(role)) return true;

        // For mapped roles
        const mappedRoles = userRoles.map(mapRoleToUI);
        return mappedRoles.includes(role);
    };

    // The context value
    const contextValue = {
        user,
        activeRole,
        userRoles,
        thrustArea,
        isAuthenticated,
        loading,
        login,
        logout,
        switchRole,
        hasRole,
        fetchUserRoles,
        authToken,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
